import { useWeb3React } from "@web3-react/core";
import React, { createContext, ReactNode, useEffect, useMemo, useReducer } from "react";
import { LSDBagTokens, LSDBagTokenType } from "../constants/LSD";
import network from "../constants/network";
import { LSDBag, LSDBags } from "../types/LSD";
import { getERC20Contract } from "../utils/contracts";
import { initialContracts } from "./ContractsContext";

interface LSDContextState {
    bags: LSDBags;
    tokens: LSDBagTokenType[];
    minBagAge: number;
    approved: string[]; // array of approved contracts
}

export const initialState = {
    bags: Array<LSDBag>(),
    tokens: LSDBagTokens,
    minBagAge: 4 * 60 * 60 * 1000, // 4hrs
    approved: [],
};

export enum LSDContextActionType {
    SET_BAGS,
    SET_APPROVED_TOKENS,
}

export type LSDContextAction =
    | {
          type: LSDContextActionType.SET_BAGS;
          payload: LSDBags;
      }
    | { type: LSDContextActionType.SET_APPROVED_TOKENS; payload: string[] };

export interface LSDContextUpdaters {
    bags: () => void;
}

const reducer = (state: LSDContextState, action: LSDContextAction) => {
    switch (action.type) {
        case LSDContextActionType.SET_BAGS:
            return { ...state, bags: action.payload };
        case LSDContextActionType.SET_APPROVED_TOKENS:
            return { ...state, approved: action.payload };
    }
};

export const LSDContext = createContext<{
    state: LSDContextState;
    dispatch: React.Dispatch<LSDContextAction>;
    update: LSDContextUpdaters;
}>({ state: initialState, dispatch: () => null, update: { bags: () => {} } });

export const LSDProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const { account } = useWeb3React();

    useEffect(() => {
        loadBags(account, dispatch);
        checkApproved(account, dispatch);
    }, [account]);

    const update = useMemo(
        () => ({
            bags: () => loadBags(account, dispatch),
        }),
        [account]
    );

    const contextValue = useMemo(() => {
        return { state, dispatch, update };
    }, [state, dispatch, update]);

    return <LSDContext.Provider value={contextValue}>{children}</LSDContext.Provider>;
};

async function loadBags(
    account: string | null | undefined,
    dispatch: React.Dispatch<LSDContextAction>
) {
    console.log("loading bags")
    let bags: LSDBags = Array<LSDBag>();
    console.log("account is", account)
    if (account) {
        console.log("if account")
        try {
            bags = await initialContracts.LSD_BAG.read.getBagsOwnedBy(account);
        } catch (e) {
            console.log("exception thrown")
            bags = [];
        }
    } else {
        bags = [];
    }
    console.log("dispatching bags", bags)
    dispatch({ type: LSDContextActionType.SET_BAGS, payload: bags });
}

function checkApproved(
    account: string | null | undefined,
    dispatch: React.Dispatch<LSDContextAction>
) {
    let approved: string[] = [];
    if (account) {
        LSDBagTokens.forEach(async (token) => {
            getERC20Contract(token.address)
                .allowance(account, network.LSDBagContractAddress)
                .then((allowance) => {
                    if (allowance.gt(0)) {
                        approved.push(token.address);
                    }
                });
        });
    }
    dispatch({ type: LSDContextActionType.SET_APPROVED_TOKENS, payload: approved });
}
